import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  IconArrowLeft,
  IconChevronDown,
  IconDeviceFloppy,
  IconFileImport,
  IconPlus,
  IconStar,
} from "@tabler/icons-react";
import { getIn, useFormik } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import "@assets/css/before_you_go/panel.css";
import "@assets/css/mode_tool/panel.css";
import InlineError from "@components/forms/InlineError";
import AppModal from "@components/modals/AppModal";
import MediaPicker from "@components/media/MediaPicker";
import useStartPointLanguages from "@hooks/start_point/useStartPointLanguages";
import { createMultimediaRequest } from "@services/multimedia/multimediaApi";
import { inferMediaType, resolveMediaUrl } from "@utils/media/mediaUrl";
import {
  createQuillEditor,
  destroyQuillEditor,
  getQuillHtml,
} from "@utils/quill/editor";
import { TOUR_SECTION_ICON_OPTIONS } from "./shared/constants";
import {
  importBeforeYouGoJsonFile,
  importMaximizeExperienceJsonFile,
} from "@utils/tour_sections/importBeforeYouGoJson";
import {
  createEmptyItem,
  createEmptySection,
  normalizeSeedSections,
  pickTourName,
  readLocalizedValue,
  reorderItemsWithSequentialOrder,
  reorderSectionsWithSequentialOrder,
} from "./shared/helpers";
import SortableSectionCard from "./shared/SortableSectionCard";
import SortableSectionItemCard from "./shared/SortableSectionItemCard";
import { buildTourSectionValidationSchema } from "./shared/validation";

const BEFORE_YOU_GO_ITEM_DESCRIPTION_MAX_CHARS = 150;

function limitQuillPlainText(quill, maxLength) {
  if (!quill || !maxLength) return;
  const text = quill.getText() || "";
  const limitWithTrailingNewline = maxLength + 1;
  if (text.length <= limitWithTrailingNewline) return;
  const selection = quill.getSelection();
  quill.deleteText(maxLength, text.length - maxLength, "silent");
  if (selection) {
    quill.setSelection(Math.min(selection.index, maxLength), 0, "silent");
  }
}

function getCountableTextFromQuill(quill) {
  if (!quill) return "";
  return (quill.getText() || "").replace(/\n$/, "");
}

function preventQuillInputOverLimit(event, quill, maxLength) {
  if (!quill || !maxLength) return;
  if (event.ctrlKey || event.metaKey || event.altKey) return;
  const allowedControlKeys = [
    "Backspace",
    "Delete",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Home",
    "End",
    "Tab",
    "Escape",
  ];
  if (allowedControlKeys.includes(event.key)) return;
  const isTextCharacter = event.key.length === 1;
  const isLineBreak = event.key === "Enter";
  if (!isTextCharacter && !isLineBreak) return;
  const selection = quill.getSelection();
  const selectedLength = selection?.length || 0;
  const currentLength = getCountableTextFromQuill(quill).length;
  if (currentLength - selectedLength + 1 > maxLength) {
    event.preventDefault();
  }
}

function getCountableEditorTextFromHtml(html = "") {
  if (typeof document === "undefined") return "";
  const element = document.createElement("div");
  element.innerHTML = html || "";
  return (element.textContent || "").replace(/\n$/, "");
}

function BaseTourSectionPanel({
  activeTour,
  languages,
  initialSections = [],
  loadingSections = false,
  panelTitle = "Before you go",
  onClose,
  onSave,
}) {
  const languageList = useStartPointLanguages(languages);
  const [activeLanguageIso, setActiveLanguageIso] = useState("");
  const [lockSaveUntilValid, setLockSaveUntilValid] = useState(false);
  const activeIsos = useMemo(
    () => (languageList || []).map((language) => language.iso).filter(Boolean),
    [languageList],
  );
  const isBeforeYouGoPanel = panelTitle
    .toLowerCase()
    .includes("before you go");
  const isMaximizeExperiencePanel = panelTitle
    .toLowerCase()
    .includes("maximize");
  const supportsJsonImport =
    isBeforeYouGoPanel || isMaximizeExperiencePanel;
  const validationSchema = useMemo(
    () => buildTourSectionValidationSchema(activeIsos),
    [activeIsos],
  );
  const [sections, setSections] = useState([]);
  const [expandedSectionId, setExpandedSectionId] = useState("");
  const [iconPickerOpenSectionId, setIconPickerOpenSectionId] = useState("");
  const [uploadingImageBySectionId, setUploadingImageBySectionId] = useState(
    {},
  );
  const [imageErrorBySectionId, setImageErrorBySectionId] = useState({});
  const [uploadingImageByItemId, setUploadingImageByItemId] = useState({});
  const [imageErrorByItemId, setImageErrorByItemId] = useState({});
  const [editor, setEditor] = useState({
    isOpen: false,
    target: "section",
    sectionId: "",
    itemId: "",
    languageIso: "",
  });
  const [editorHtml, setEditorHtml] = useState("");
  const [quillHost, setQuillHost] = useState(null);
  const [isDescriptionEditorReady, setIsDescriptionEditorReady] =
    useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isImportingJson, setIsImportingJson] = useState(false);
  const [importJsonError, setImportJsonError] = useState("");
  const [importJsonProgress, setImportJsonProgress] = useState("");
  const importJsonInputRef = useRef(null);
  const [quillState, setQuillState] = useState({
    instance: null,
    handler: null,
  });
  const formik = useFormik({
    initialValues: {
      sections: [],
    },
    validateOnMount: true,
    validationSchema,
    onSubmit: () => {},
  });
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  useEffect(() => {
    if (!languageList.length) {
      setActiveLanguageIso("");
      return;
    }
    setActiveLanguageIso((currentIso) => {
      if (languageList.some((language) => language.iso === currentIso)) {
        return currentIso;
      }
      return languageList[0].iso;
    });
  }, [languageList]);

  useEffect(() => {
    if (!activeIsos.length) return;
    const normalized = normalizeSeedSections(initialSections, activeIsos);
    setSections(normalized);
    setExpandedSectionId("");
    formik.setFieldValue("sections", normalized, true);
  }, [activeIsos, initialSections]);

  useEffect(() => {
    if (lockSaveUntilValid && formik.isValid) {
      setLockSaveUntilValid(false);
    }
  }, [formik.isValid, lockSaveUntilValid]);

  const visibleLanguageIso = useMemo(() => {
    if (activeIsos.includes(activeLanguageIso)) return activeLanguageIso;
    return activeIsos[0] || "";
  }, [activeIsos, activeLanguageIso]);
  const shouldLimitActiveEditor =
    isBeforeYouGoPanel && editor.isOpen && editor.target === "item";
  const editorPlainTextLength = useMemo(() => {
    if (!shouldLimitActiveEditor) return 0;
    if (quillState.instance) {
      return getCountableTextFromQuill(quillState.instance).length;
    }
    return getCountableEditorTextFromHtml(editorHtml).length;
  }, [editorHtml, quillState.instance, shouldLimitActiveEditor]);
  const requiredLanguageIso = useMemo(() => {
    if (activeIsos.includes("en")) return "en";
    return activeIsos[0] || "";
  }, [activeIsos]);

  useEffect(() => {
    if (!editor.isOpen) return;
    if (!quillHost || quillState.instance) return;
    const quill = createQuillEditor({
      container: quillHost,
      initialHtml: editorHtml,
    });
    if (!quill) return;
    const shouldLimitItemDescription =
      isBeforeYouGoPanel && editor.target === "item";
    const keydownHandler = (event) => {
      if (!shouldLimitItemDescription) return;
      preventQuillInputOverLimit(
        event,
        quill,
        BEFORE_YOU_GO_ITEM_DESCRIPTION_MAX_CHARS,
      );
    };
    const handler = () => {
      if (shouldLimitItemDescription) {
        limitQuillPlainText(quill, BEFORE_YOU_GO_ITEM_DESCRIPTION_MAX_CHARS);
      }
      setEditorHtml(getQuillHtml(quill));
    };
    if (shouldLimitItemDescription) {
      limitQuillPlainText(quill, BEFORE_YOU_GO_ITEM_DESCRIPTION_MAX_CHARS);
    }
    quill.root.addEventListener("keydown", keydownHandler);
    quill.on("text-change", handler);
    setQuillState({ instance: quill, handler, keydownHandler });
    setIsDescriptionEditorReady(true);
  }, [
    editor.isOpen,
    editor.target,
    editorHtml,
    isBeforeYouGoPanel,
    quillHost,
    quillState.instance,
  ]);

  useEffect(() => {
    if (editor.isOpen || !quillState.instance) return;
    destroyQuillEditor({
      container: quillHost,
      quill: quillState.instance,
      textChangeHandler: quillState.handler,
    });
    if (quillState.keydownHandler) {
      quillState.instance.root.removeEventListener(
        "keydown",
        quillState.keydownHandler,
      );
    }
    setQuillState({ instance: null, handler: null, keydownHandler: null });
    setIsDescriptionEditorReady(false);
    setQuillHost(null);
  }, [editor.isOpen, quillHost, quillState]);

  if (!activeTour) return null;

  function updateSections(nextSectionsOrUpdater, shouldValidate = true) {
    setSections((currentSections) => {
      const nextSections =
        typeof nextSectionsOrUpdater === "function"
          ? nextSectionsOrUpdater(currentSections)
          : nextSectionsOrUpdater;
      formik.setFieldValue("sections", nextSections, shouldValidate);
      return nextSections;
    });
  }

  function updateSection(sectionId, updater) {
    updateSections((currentSections) =>
      currentSections.map((section) =>
        section.id === sectionId ? updater(section) : section,
      ),
    );
  }

  function updateSectionField(sectionId, field, value) {
    updateSection(sectionId, (section) => ({ ...section, [field]: value }));
  }

  function updateSectionLocalizedField(sectionId, field, iso, value) {
    updateSection(sectionId, (section) => ({
      ...section,
      [field]: {
        ...(section?.[field] || {}),
        [iso]: value,
      },
    }));
  }

  function updateItem(sectionId, itemId, updater) {
    updateSection(sectionId, (section) => ({
      ...section,
      items: (section.items || []).map((item) =>
        item.id === itemId ? updater(item) : item,
      ),
    }));
  }

  function updateItemField(sectionId, itemId, field, value) {
    updateItem(sectionId, itemId, (item) => ({ ...item, [field]: value }));
  }

  function updateItemLocalizedField(sectionId, itemId, field, iso, value) {
    updateItem(sectionId, itemId, (item) => ({
      ...item,
      [field]: {
        ...(item?.[field] || {}),
        [iso]: value,
      },
    }));
  }

  function handleAddSection() {
    const nextSection = createEmptySection(sections, activeIsos);
    updateSections((currentSections) => [...currentSections, nextSection]);
    setExpandedSectionId(nextSection.id);
  }

  async function handleImportTourSectionJson(file) {
    if (!supportsJsonImport || !file || isImportingJson) return;
    const importLabel = isBeforeYouGoPanel
      ? "Before You Go"
      : "Maximize Your Experience";
    if (
      sections.length &&
      !window.confirm(
        `Are you sure you want to replace the current ${importLabel} sections with this JSON file?`,
      )
    ) {
      return;
    }

    setIsImportingJson(true);
    setImportJsonError("");
    setImportJsonProgress("Reading JSON file...");
    try {
      const importer = isBeforeYouGoPanel
        ? importBeforeYouGoJsonFile
        : importMaximizeExperienceJsonFile;
      const importedSections = await importer(
        file,
        activeIsos,
        setImportJsonProgress,
      );
      updateSections(importedSections, true);
      setExpandedSectionId(importedSections[0]?.id || "");
      if (activeIsos.includes("en")) setActiveLanguageIso("en");
    } catch (error) {
      setImportJsonError(
        error?.message || `Could not import ${importLabel} JSON.`,
      );
      setImportJsonProgress("");
    } finally {
      setIsImportingJson(false);
    }
  }

  function handleRemoveSection(sectionId) {
    updateSections((currentSections) =>
      currentSections
        .filter((section) => section.id !== sectionId)
        .map((section, index) => ({
          ...section,
          order: index + 1,
        })),
    );
    setExpandedSectionId((currentId) =>
      currentId === sectionId ? "" : currentId,
    );
  }

  function handleDragEnd(event) {
    const { active, over } = event || {};
    const activeId = String(active?.id || "");
    const overId = String(over?.id || "");
    if (!activeId || !overId || activeId === overId) return;
    updateSections((currentSections) => {
      const oldIndex = currentSections.findIndex(
        (section) => String(section.id) === activeId,
      );
      const newIndex = currentSections.findIndex(
        (section) => String(section.id) === overId,
      );
      if (oldIndex < 0 || newIndex < 0) return currentSections;
      return reorderSectionsWithSequentialOrder(
        currentSections,
        oldIndex,
        newIndex,
      );
    });
  }

  function handleItemDragEnd(sectionId, event) {
    const { active, over } = event || {};
    const activeId = String(active?.id || "");
    const overId = String(over?.id || "");
    if (!activeId || !overId || activeId === overId) return;
    updateSection(sectionId, (section) => {
      const items = Array.isArray(section?.items) ? section.items : [];
      const oldIndex = items.findIndex((item) => String(item.id) === activeId);
      const newIndex = items.findIndex((item) => String(item.id) === overId);
      if (oldIndex < 0 || newIndex < 0) return section;
      return {
        ...section,
        items: reorderItemsWithSequentialOrder(items, oldIndex, newIndex),
      };
    });
  }

  function handleAddItem(sectionId, itemType = "hotel") {
    updateSection(sectionId, (section) => ({
      ...section,
      items: [
        ...(section.items || []),
        createEmptyItem(sectionId, section.items, activeIsos, itemType),
      ],
    }));
  }

  function handleRemoveItem(sectionId, itemId) {
    updateSection(sectionId, (section) => ({
      ...section,
      items: (section.items || [])
        .filter((item) => item.id !== itemId)
        .map((item, index) => ({
          ...item,
          order: index + 1,
          displayOrder: index + 1,
        })),
    }));
  }

  async function handleUploadSectionImage(sectionId, files) {
    if (!Array.isArray(files) || !files.length) return;
    setUploadingImageBySectionId((prev) => ({ ...prev, [sectionId]: true }));
    setImageErrorBySectionId((prev) => ({ ...prev, [sectionId]: "" }));
    try {
      const response = await createMultimediaRequest({ file: files[0] });
      const body = response?.body || {};
      const rawItem = Array.isArray(body?.multimediaList)
        ? body.multimediaList[0]
        : Array.isArray(body?.multimedia)
          ? body.multimedia[0]
          : body?.multimedia || null;
      if (!rawItem) throw new Error("Could not upload image.");
      const rawUrl = rawItem?.url || rawItem?.mmd_url || "";
      updateSection(sectionId, (section) => ({
        ...section,
        imageUrl: resolveMediaUrl(rawUrl),
        image: {
          id: rawItem?.id || rawItem?.mmd_id || `${sectionId}-image`,
          url: resolveMediaUrl(rawUrl),
          mediaType:
            rawItem?.mediaType ||
            rawItem?.mmd_media_type ||
            inferMediaType(rawUrl),
          mimeType: rawItem?.mimeType || rawItem?.mmd_mime_type || "image/*",
        },
      }));
    } catch (error) {
      setImageErrorBySectionId((prev) => ({
        ...prev,
        [sectionId]: error?.message || "Could not upload image.",
      }));
    } finally {
      setUploadingImageBySectionId((prev) => ({ ...prev, [sectionId]: false }));
    }
  }

  async function handleUploadItemImage(sectionId, itemId, files) {
    if (!Array.isArray(files) || !files.length) return;
    setUploadingImageByItemId((prev) => ({ ...prev, [itemId]: true }));
    setImageErrorByItemId((prev) => ({ ...prev, [itemId]: "" }));
    try {
      const response = await createMultimediaRequest({ file: files[0] });
      const body = response?.body || {};
      const rawItem = Array.isArray(body?.multimediaList)
        ? body.multimediaList[0]
        : Array.isArray(body?.multimedia)
          ? body.multimedia[0]
          : body?.multimedia || null;
      if (!rawItem) throw new Error("Could not upload image.");
      const rawUrl = rawItem?.url || rawItem?.mmd_url || "";
      updateItem(sectionId, itemId, (item) => ({
        ...item,
        imageUrl: resolveMediaUrl(rawUrl),
        image: {
          id: rawItem?.id || rawItem?.mmd_id || `${itemId}-image`,
          url: resolveMediaUrl(rawUrl),
          mediaType:
            rawItem?.mediaType ||
            rawItem?.mmd_media_type ||
            inferMediaType(rawUrl),
          mimeType: rawItem?.mimeType || rawItem?.mmd_mime_type || "image/*",
        },
      }));
    } catch (error) {
      setImageErrorByItemId((prev) => ({
        ...prev,
        [itemId]: error?.message || "Could not upload image.",
      }));
    } finally {
      setUploadingImageByItemId((prev) => ({ ...prev, [itemId]: false }));
    }
  }

  function openDescriptionEditor(sectionId, languageIso) {
    const section = sections.find((item) => item.id === sectionId);
    setEditor({
      isOpen: true,
      target: "section",
      sectionId,
      itemId: "",
      languageIso,
    });
    setEditorHtml(readLocalizedValue(section?.descriptions?.[languageIso]));
  }

  function openItemDescriptionEditor(sectionId, itemId, languageIso) {
    const section = sections.find((item) => item.id === sectionId);
    const item = (section?.items || []).find((entry) => entry.id === itemId);
    setEditor({
      isOpen: true,
      target: "item",
      sectionId,
      itemId,
      languageIso,
    });
    setEditorHtml(readLocalizedValue(item?.description?.[languageIso]));
  }

  function closeDescriptionEditor() {
    setEditor({
      isOpen: false,
      target: "section",
      sectionId: "",
      itemId: "",
      languageIso: "",
    });
    setEditorHtml("");
    setIsDescriptionEditorReady(false);
  }

  function saveDescriptionEditor() {
    if (!editor.sectionId) return;
    if (editor.target === "item") {
      if (!editor.itemId || !editor.languageIso) return;
      if (isBeforeYouGoPanel && quillState.instance) {
        limitQuillPlainText(
          quillState.instance,
          BEFORE_YOU_GO_ITEM_DESCRIPTION_MAX_CHARS,
        );
      }
      updateItemLocalizedField(
        editor.sectionId,
        editor.itemId,
        "description",
        editor.languageIso,
        isBeforeYouGoPanel && quillState.instance
          ? getQuillHtml(quillState.instance)
          : editorHtml || "",
      );
      closeDescriptionEditor();
      return;
    }
    if (!editor.languageIso) return;
    updateSectionLocalizedField(
      editor.sectionId,
      "descriptions",
      editor.languageIso,
      editorHtml,
    );
    formik.setFieldTouched(
      `sections.${sections.findIndex((item) => item.id === editor.sectionId)}.descriptions.${editor.languageIso}`,
      true,
      true,
    );
    closeDescriptionEditor();
  }

  async function handleSaveBeforeYouGo() {
    const errors = await formik.validateForm();
    if (Object.keys(errors || {}).length) {
      touchAllRequiredFields();
      setLockSaveUntilValid(true);
      return;
    }
    if (typeof onSave !== "function") return;
    setIsSaving(true);
    try {
      await onSave({
        tour: activeTour,
        sections,
      });
    } catch (error) {
    } finally {
      setIsSaving(false);
    }
  }

  function getFieldError(path) {
    if (visibleLanguageIso !== requiredLanguageIso) return "";
    const errorValue = getIn(formik.errors, path);
    return errorValue ? String(errorValue) : "";
  }

  function touchAllRequiredFields() {
    formik.setTouched({
      sections: sections.map((section) => ({
        title: requiredLanguageIso ? { [requiredLanguageIso]: true } : {},
        iconName: true,
        imageUrl: true,
        descriptions: requiredLanguageIso
          ? { [requiredLanguageIso]: true }
          : {},
        items: (section.items || []).map(() => ({
          itemType: true,
          title: requiredLanguageIso ? { [requiredLanguageIso]: true } : {},
          location: true,
          rating: true,
          websiteUrl: true,
          imageUrl: true,
        })),
      })),
    });
  }

  return (
    <aside className="before-you-go-tool-overlay">
      <div className="mode-route-tool-header before-you-go-tool-header">
        {languageList.length ? (
          <div className="app-language-section">
            <div className="app-language-switcher" aria-label="Languages">
              {languageList.map((language) => (
                <button
                  key={language.id || language.iso}
                  type="button"
                  className={`app-language-btn ${visibleLanguageIso === language.iso ? "is-active" : ""}`}
                  onClick={() => setActiveLanguageIso(language.iso)}
                  aria-pressed={visibleLanguageIso === language.iso}
                  title={language.name || language.iso.toUpperCase()}
                >
                  {language.iso.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        <div className="mode-route-tool-head">
          <button
            type="button"
            className="app-icon-btn app-icon-btn--back"
            aria-label="Go back"
            title="Go back"
            onClick={onClose}
          >
            <IconArrowLeft size={16} />
          </button>
          {supportsJsonImport ? (
            <>
              <input
                ref={importJsonInputRef}
                type="file"
                accept=".json,application/json"
                hidden
                onChange={(event) => {
                  const file = event.target.files?.[0] || null;
                  handleImportTourSectionJson(file);
                  event.target.value = "";
                }}
              />
              <button
                type="button"
                className={`app-icon-btn app-icon-btn--update ${isImportingJson ? "is-loading" : ""}`}
                aria-label={`Import ${panelTitle} JSON`}
                title={`Import ${panelTitle} JSON`}
                disabled={isImportingJson}
                onClick={() => importJsonInputRef.current?.click()}
              >
                {isImportingJson ? (
                  <span className="mode-route-tool-btn-spinner" />
                ) : (
                  <IconFileImport size={17} />
                )}
              </button>
            </>
          ) : null}
          <button
            type="button"
            className="app-icon-btn app-icon-btn--update"
            aria-label="Add section"
            title="Add section"
            onClick={handleAddSection}
          >
            <IconPlus size={18} />
          </button>
          <h3 className="mode-route-tool-title before-you-go-tool-title">
            {panelTitle} - {pickTourName(activeTour)}
          </h3>
        </div>
      </div>

      <section className="before-you-go-tool-panel">
        {importJsonError ? (
          <p className="message-error before-you-go-tool-import-message">
            {importJsonError}
          </p>
        ) : null}
        {isImportingJson && importJsonProgress ? (
          <p className="before-you-go-tool-help before-you-go-tool-import-message">
            {importJsonProgress}
          </p>
        ) : null}
        {loadingSections ? (
          <p className="before-you-go-tool-help">Loading before you go...</p>
        ) : null}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sections.map((section) => section.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="before-you-go-tool-list">
              {sections.map((section) => {
                const isOpen = expandedSectionId === section.id;
                const CurrentSectionIcon =
                  TOUR_SECTION_ICON_OPTIONS.find(
                    (option) => option.value === (section?.iconName || "star"),
                  )?.Icon || IconStar;

                return (
                  <SortableSectionCard
                    key={section.id}
                    section={section}
                    visibleLanguageIso={visibleLanguageIso}
                    isOpen={isOpen}
                    onToggle={() =>
                      setExpandedSectionId((currentId) =>
                        currentId === section.id ? "" : section.id,
                      )
                    }
                    onDelete={() => handleRemoveSection(section.id)}
                  >
                    {isOpen ? (
                      <div className="before-you-go-tool-form">
                        <div>
                          <p className="tour-add-form-label">
                            title
                            {getFieldError(
                              `sections.${sections.findIndex((entry) => entry.id === section.id)}.title.${visibleLanguageIso}`,
                            ) ? (
                              <InlineError>
                                {getFieldError(
                                  `sections.${sections.findIndex((entry) => entry.id === section.id)}.title.${visibleLanguageIso}`,
                                )}
                              </InlineError>
                            ) : null}
                          </p>
                          <label className="field-label app-form-field">
                            <input
                              className="field-input"
                              value={section?.title?.[visibleLanguageIso] || ""}
                              onChange={(event) =>
                                updateSectionLocalizedField(
                                  section.id,
                                  "title",
                                  visibleLanguageIso,
                                  event.target.value,
                                )
                              }
                              onBlur={() =>
                                formik.setFieldTouched(
                                  `sections.${sections.findIndex((entry) => entry.id === section.id)}.title.${visibleLanguageIso}`,
                                  true,
                                  true,
                                )
                              }
                            />
                          </label>
                        </div>

                        <div className="before-you-go-tool-icon-field">
                          <span className="label-inline before-you-go-tool-icon-label">
                            Icon
                          </span>
                          <div className="before-you-go-tool-icon-picker">
                            <button
                              type="button"
                              className="before-you-go-tool-icon-trigger"
                              onClick={() =>
                                setIconPickerOpenSectionId((currentId) =>
                                  currentId === section.id ? "" : section.id,
                                )
                              }
                            >
                              <span className="before-you-go-tool-icon-trigger-main">
                                <CurrentSectionIcon size={15} />
                                <span>
                                  {TOUR_SECTION_ICON_OPTIONS.find(
                                    (option) =>
                                      option.value ===
                                      (section.iconName || "star"),
                                  )?.label || "Star"}
                                </span>
                              </span>
                              <IconChevronDown size={14} />
                            </button>
                            {iconPickerOpenSectionId === section.id ? (
                              <div className="before-you-go-tool-icon-menu">
                                {TOUR_SECTION_ICON_OPTIONS.map((option) => {
                                  const OptionIcon = option.Icon;
                                  const isActive =
                                    option.value ===
                                    (section.iconName || "star");
                                  return (
                                    <button
                                      key={option.value}
                                      type="button"
                                      className={`before-you-go-tool-icon-item ${isActive ? "active" : ""}`}
                                      onClick={() => {
                                        updateSectionField(
                                          section.id,
                                          "iconName",
                                          option.value,
                                        );
                                        setIconPickerOpenSectionId("");
                                      }}
                                    >
                                      <OptionIcon size={14} />
                                      <span>{option.label}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <MediaPicker
                          title="image (required)"
                          titleError={
                            getFieldError(
                              `sections.${sections.findIndex((entry) => entry.id === section.id)}.imageUrl`,
                            ) || ""
                          }
                          items={section?.image ? [section.image] : []}
                          isUploading={Boolean(
                            uploadingImageBySectionId[section.id],
                          )}
                          uploadError={imageErrorBySectionId[section.id] || ""}
                          accept={{ "image/*": [] }}
                          maxFiles={1}
                          invalidTypeMessage="Image only for this section."
                          onUpload={(files) =>
                            handleUploadSectionImage(section.id, files)
                          }
                          onRemove={() =>
                            updateSection(section.id, (currentSection) => ({
                              ...currentSection,
                              imageUrl: "",
                              image: null,
                            }))
                          }
                          onRejected={(message) => {
                            setImageErrorBySectionId((prev) => ({
                              ...prev,
                              [section.id]: message,
                            }));
                            formik.setFieldTouched(
                              `sections.${sections.findIndex((entry) => entry.id === section.id)}.imageUrl`,
                              true,
                              true,
                            );
                          }}
                          uploadLabel="Upload section image"
                          uploadHint="Only 1 image is allowed for this section."
                        />

                        <div>
                          <p className="tour-add-form-label">description</p>
                          <button
                            type="button"
                            className={`field-label app-form-field field-preview-row ${readLocalizedValue(section?.descriptions?.[visibleLanguageIso]) ? "filled" : ""}`}
                            onClick={() =>
                              openDescriptionEditor(
                                section.id,
                                visibleLanguageIso,
                              )
                            }
                          >
                            {readLocalizedValue(
                              section?.descriptions?.[visibleLanguageIso],
                            ) ? (
                              <span
                                className="field-preview-value field-preview-value-html"
                                dangerouslySetInnerHTML={{
                                  __html: readLocalizedValue(
                                    section?.descriptions?.[visibleLanguageIso],
                                  ),
                                }}
                              />
                            ) : (
                              <span className="field-preview-value">
                                Complete
                              </span>
                            )}
                          </button>
                          {getFieldError(
                            `sections.${sections.findIndex((entry) => entry.id === section.id)}.descriptions.${visibleLanguageIso}`,
                          ) ? (
                            <InlineError>
                              {getFieldError(
                                `sections.${sections.findIndex((entry) => entry.id === section.id)}.descriptions.${visibleLanguageIso}`,
                              )}
                            </InlineError>
                          ) : null}
                        </div>

                        <div className="before-you-go-tool-items-head">
                          <strong>Items</strong>
                          <div className="before-you-go-tool-inline-actions">
                            <button
                              type="button"
                              className="app-icon-btn app-icon-btn--add before-you-go-tool-add-item-btn"
                              aria-label="Add item"
                              title="Add item"
                              onClick={() => handleAddItem(section.id)}
                            >
                              <IconPlus size={14} />
                            </button>
                          </div>
                        </div>

                        {Array.isArray(section.items) &&
                        section.items.length ? (
                          <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={(event) =>
                              handleItemDragEnd(section.id, event)
                            }
                          >
                            <SortableContext
                              items={section.items.map((item) => item.id)}
                              strategy={verticalListSortingStrategy}
                            >
                              <div className="before-you-go-tool-items-list">
                                {section.items.map((item) => (
                                  <SortableSectionItemCard
                                    key={item.id}
                                    item={item}
                                    visibleLanguageIso={visibleLanguageIso}
                                    onDelete={() =>
                                      handleRemoveItem(section.id, item.id)
                                    }
                                  >
                                    {isBeforeYouGoPanel ? (
                                      <div className="before-you-go-tool-item-grid">
                                        <label className="field-label app-form-field">
                                          <span className="label-inline">
                                            Type
                                            {getFieldError(
                                              `sections.${sections.findIndex((entry) => entry.id === section.id)}.items.${section.items.findIndex((entry) => entry.id === item.id)}.itemType`,
                                            ) ? (
                                              <InlineError>
                                                {getFieldError(
                                                  `sections.${sections.findIndex((entry) => entry.id === section.id)}.items.${section.items.findIndex((entry) => entry.id === item.id)}.itemType`,
                                                )}
                                              </InlineError>
                                            ) : null}
                                          </span>
                                          <select
                                            className="field-input select-control"
                                            value={item.itemType}
                                            onChange={(event) =>
                                              updateItemField(
                                                section.id,
                                                item.id,
                                                "itemType",
                                                event.target.value,
                                              )
                                            }
                                            onBlur={() =>
                                              formik.setFieldTouched(
                                                `sections.${sections.findIndex((entry) => entry.id === section.id)}.items.${section.items.findIndex((entry) => entry.id === item.id)}.itemType`,
                                                true,
                                                true,
                                              )
                                            }
                                          >
                                            <option value="hotel">Hotel</option>
                                            <option value="rentalCar">
                                              Rental Car
                                            </option>
                                          </select>
                                        </label>
                                      </div>
                                    ) : null}

                                    <div>
                                      <p className="tour-add-form-label">
                                        title
                                        {getFieldError(
                                          `sections.${sections.findIndex((entry) => entry.id === section.id)}.items.${section.items.findIndex((entry) => entry.id === item.id)}.title.${visibleLanguageIso}`,
                                        ) ? (
                                          <InlineError>
                                            {getFieldError(
                                              `sections.${sections.findIndex((entry) => entry.id === section.id)}.items.${section.items.findIndex((entry) => entry.id === item.id)}.title.${visibleLanguageIso}`,
                                            )}
                                          </InlineError>
                                        ) : null}
                                      </p>
                                      <label className="field-label app-form-field">
                                        <input
                                          className="field-input"
                                          value={
                                            item?.title?.[visibleLanguageIso] ||
                                            ""
                                          }
                                          onChange={(event) =>
                                            updateItemLocalizedField(
                                              section.id,
                                              item.id,
                                              "title",
                                              visibleLanguageIso,
                                              event.target.value,
                                            )
                                          }
                                          onBlur={() =>
                                            formik.setFieldTouched(
                                              `sections.${sections.findIndex((entry) => entry.id === section.id)}.items.${section.items.findIndex((entry) => entry.id === item.id)}.title.${visibleLanguageIso}`,
                                              true,
                                              true,
                                            )
                                          }
                                        />
                                      </label>
                                    </div>

                                    <label className="field-label app-form-field">
                                      <span className="label-inline">
                                        Location
                                        {getFieldError(
                                          `sections.${sections.findIndex((entry) => entry.id === section.id)}.items.${section.items.findIndex((entry) => entry.id === item.id)}.location`,
                                        ) ? (
                                          <InlineError>
                                            {getFieldError(
                                              `sections.${sections.findIndex((entry) => entry.id === section.id)}.items.${section.items.findIndex((entry) => entry.id === item.id)}.location`,
                                            )}
                                          </InlineError>
                                        ) : null}
                                      </span>
                                      <input
                                        className="field-input"
                                        value={item.location}
                                        onChange={(event) =>
                                          updateItemField(
                                            section.id,
                                            item.id,
                                            "location",
                                            event.target.value,
                                          )
                                        }
                                        onBlur={() =>
                                          formik.setFieldTouched(
                                            `sections.${sections.findIndex((entry) => entry.id === section.id)}.items.${section.items.findIndex((entry) => entry.id === item.id)}.location`,
                                            true,
                                            true,
                                          )
                                        }
                                      />
                                    </label>

                                    <div>
                                      <p className="tour-add-form-label">
                                        description
                                      </p>
                                      <button
                                        type="button"
                                        className={`field-label app-form-field field-preview-row ${readLocalizedValue(item?.description?.[visibleLanguageIso]) ? "filled" : ""}`}
                                        onClick={() =>
                                          openItemDescriptionEditor(
                                            section.id,
                                            item.id,
                                            visibleLanguageIso,
                                          )
                                        }
                                      >
                                        {readLocalizedValue(
                                          item?.description?.[
                                            visibleLanguageIso
                                          ],
                                        ) ? (
                                          <span
                                            className="field-preview-value field-preview-value-html"
                                            dangerouslySetInnerHTML={{
                                              __html: readLocalizedValue(
                                                item?.description?.[
                                                  visibleLanguageIso
                                                ],
                                              ),
                                            }}
                                          />
                                        ) : (
                                          <span className="field-preview-value">
                                            Complete
                                          </span>
                                        )}
                                      </button>
                                    </div>

                                    <label className="field-label app-form-field">
                                      <span className="label-inline">
                                        Website URL
                                        {getFieldError(
                                          `sections.${sections.findIndex((entry) => entry.id === section.id)}.items.${section.items.findIndex((entry) => entry.id === item.id)}.websiteUrl`,
                                        ) ? (
                                          <InlineError>
                                            {getFieldError(
                                              `sections.${sections.findIndex((entry) => entry.id === section.id)}.items.${section.items.findIndex((entry) => entry.id === item.id)}.websiteUrl`,
                                            )}
                                          </InlineError>
                                        ) : null}
                                      </span>
                                      <input
                                        className="field-input"
                                        value={item.websiteUrl}
                                        onChange={(event) =>
                                          updateItemField(
                                            section.id,
                                            item.id,
                                            "websiteUrl",
                                            event.target.value,
                                          )
                                        }
                                        onBlur={() =>
                                          formik.setFieldTouched(
                                            `sections.${sections.findIndex((entry) => entry.id === section.id)}.items.${section.items.findIndex((entry) => entry.id === item.id)}.websiteUrl`,
                                            true,
                                            true,
                                          )
                                        }
                                      />
                                    </label>

                                    <MediaPicker
                                      title="image"
                                      titleError={
                                        getFieldError(
                                          `sections.${sections.findIndex((entry) => entry.id === section.id)}.items.${section.items.findIndex((entry) => entry.id === item.id)}.imageUrl`,
                                        ) || ""
                                      }
                                      items={item?.image ? [item.image] : []}
                                      isUploading={Boolean(
                                        uploadingImageByItemId[item.id],
                                      )}
                                      uploadError={
                                        imageErrorByItemId[item.id] || ""
                                      }
                                      accept={{ "image/*": [] }}
                                      maxFiles={1}
                                      invalidTypeMessage="Image only for this item."
                                      onRejected={(message) => {
                                        setImageErrorByItemId((prev) => ({
                                          ...prev,
                                          [item.id]: message,
                                        }));
                                        formik.setFieldTouched(
                                          `sections.${sections.findIndex((entry) => entry.id === section.id)}.items.${section.items.findIndex((entry) => entry.id === item.id)}.imageUrl`,
                                          true,
                                          true,
                                        );
                                      }}
                                      onUpload={(files) =>
                                        handleUploadItemImage(
                                          section.id,
                                          item.id,
                                          files,
                                        )
                                      }
                                      onRemove={() =>
                                        updateItem(
                                          section.id,
                                          item.id,
                                          (currentItem) => ({
                                            ...currentItem,
                                            imageUrl: "",
                                            image: null,
                                          }),
                                        )
                                      }
                                      uploadLabel="Upload item image"
                                      uploadHint="Only 1 image is allowed for this item."
                                    />
                                  </SortableSectionItemCard>
                                ))}
                              </div>
                            </SortableContext>
                          </DndContext>
                        ) : (
                          <p className="before-you-go-tool-empty">
                            This section has no items yet.
                          </p>
                        )}
                      </div>
                    ) : null}
                  </SortableSectionCard>
                );
              })}
            </div>
          </SortableContext>
        </DndContext>

      </section>

      <div className="mode-route-tool-steps">
        <button
          type="button"
          className={`app-icon-btn app-icon-btn--add mode-route-tool-route-btn ${isSaving || isImportingJson || typeof onSave !== "function" ? "is-disabled" : ""} ${isSaving || isImportingJson ? "is-loading" : ""}`}
          aria-label={
            isSaving
              ? `Saving ${String(panelTitle || "").toLowerCase()}`
              : `Save ${String(panelTitle || "").toLowerCase()}`
          }
          title={
            isSaving
              ? `Saving ${String(panelTitle || "").toLowerCase()}`
              : `Save ${String(panelTitle || "").toLowerCase()}`
          }
          onClick={handleSaveBeforeYouGo}
          disabled={
            isSaving ||
            isImportingJson ||
            typeof onSave !== "function" ||
            !formik.isValid
          }
        >
          {isSaving ? (
            <span className="mode-route-tool-btn-spinner" />
          ) : (
            <>
              Save
              <IconDeviceFloppy size={14} />
            </>
          )}
        </button>
      </div>

      <AppModal
        isOpen={editor.isOpen}
        onClose={closeDescriptionEditor}
        title={
          editor.target === "item"
            ? `description (${editor.languageIso})`
            : `description (${editor.languageIso})`
        }
        size="lg"
      >
        <div className="tour-add-editor">
          {!isDescriptionEditorReady ? (
            <p className="tour-add-editor-loading">Loading editor...</p>
          ) : null}
          <div ref={setQuillHost} className="tour-add-quill" />
          {shouldLimitActiveEditor ? (
            <small className="field-counter">
              {Math.min(
                editorPlainTextLength,
                BEFORE_YOU_GO_ITEM_DESCRIPTION_MAX_CHARS,
              )}
              /{BEFORE_YOU_GO_ITEM_DESCRIPTION_MAX_CHARS} characters
            </small>
          ) : null}
          <div className="tour-add-editor-actions">
            <button
              type="button"
              className="button-primary"
              onClick={saveDescriptionEditor}
            >
              Save
            </button>
            <button
              type="button"
              className="button-ghost"
              onClick={closeDescriptionEditor}
            >
              Cancel
            </button>
          </div>
        </div>
      </AppModal>
    </aside>
  );
}

export default BaseTourSectionPanel;
